import type { ExpenseResource, RecordExpenseDto } from '@repo/api-client';
import { z } from 'zod';

import { INSTANT_MALFORMED, isoInstantFrom } from '@/lib/local-instant';
import { isRepresentablePaise, paiseFromRupees, RUPEES_SHAPE } from '@/lib/money';

import { SALE_PAYMENT_MODES, type SalePaymentMode } from '../sales/payment-modes';

/**
 * What the expense form accepts, and the two things it decides that the screen
 * cannot: which of the API's two successes an answer was, and what one attempt
 * at this write is.
 *
 * Kept beside the screen rather than in `lib/field-schemas.ts` because nothing
 * else in the app holds these rules. The amount is deliberately **not**
 * `priceInRupeesSchema` from that file, and the difference is a refusal rather
 * than a preference: a catalogue price of zero is a free child ticket and the
 * API accepts it, while an expense of zero is refused `422
 * EXPENSE_AMOUNT_INVALID`. Sharing the fragment would mean either loosening the
 * price field or refusing a free ticket, and its own note says the temptation to
 * invent a rule is strongest exactly there.
 */

/* -------------------------------------------------------------------------- */
/* The payment mode                                                            */
/* -------------------------------------------------------------------------- */

/**
 * How an expense was paid, taken from the vocabulary the sales list already
 * holds rather than spelled again here.
 *
 * R-706 is explicit that this is "the shipped payment vocabulary, not a second
 * one", and `packages/api-client` publishes the union on both resources. So the
 * three labels a person reads — "Cash", "UPI", "Card" — are read from
 * `routes/sales/payment-modes.ts`, which is the back-office copy of them.
 *
 * **The cross-epic import is deliberate and is the lesser of two faults.**
 * `lib/instant.ts` sets out the rule: reaching across `routes/<epic>/` for a
 * module named after another epic is coupling, and the answer when a second
 * epic wants one is to move it to `lib/`. That move is not made here because
 * `routes/till/payment-modes.ts` is a *third* copy of the same three labels,
 * owned by a different shell — lifting two of the three and leaving the third
 * would be a half-finished merge, and the till is live territory. Recorded in
 * the PR body for the ticket that can see all three. What is certain is that a
 * fourth copy is not the answer.
 */
export type ExpensePaymentMode = ExpenseResource['paymentMode'];

/**
 * The compiler holding R-706 to its word.
 *
 * If the expense route ever publishes a mode the sale route does not, or the
 * other way round, this stops compiling here rather than rendering `WALLET` in
 * a manager's table or offering a mode the API refuses. Nothing restates either
 * union; this only asserts they are the same one.
 */
type _SameVocabulary = [ExpensePaymentMode] extends [SalePaymentMode]
  ? [SalePaymentMode] extends [ExpensePaymentMode]
    ? true
    : never
  : never;
const _vocabularyIsShared: _SameVocabulary = true;
void _vocabularyIsShared;

/** The modes, in the order they are offered. Cash first — see the sales copy. */
export const EXPENSE_PAYMENT_MODES: readonly ExpensePaymentMode[] = SALE_PAYMENT_MODES;

export const isExpensePaymentMode = (value: string): value is ExpensePaymentMode =>
  (EXPENSE_PAYMENT_MODES as readonly string[]).includes(value);

/* -------------------------------------------------------------------------- */
/* Which success an answer was                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `201` recorded it. `200` means **already** recorded — this exact expense
 * landed before, and the server answered with the original outcome without
 * writing anything further.
 *
 * The same distinction `apps/desktop/src/main/sync/errors.ts` draws for the
 * outbox, in the same words, because it is the same fact about the same write
 * path. It is not folded in with that one: this is a browser screen and that is
 * an Electron queue, and the two share no module today.
 *
 * **Both are success and neither is a failure.** A screen that flattens them
 * says "Recorded" twice for one expense — while the server did exactly the right
 * thing — and a person who sees that goes looking for the duplicate row that is
 * not there.
 */
export type RecordOutcome = 'recorded' | 'already-recorded';

/**
 * Decided from the status and from nothing else.
 *
 * The document is emphatic about why: the `200` body "is identical to the 201 it
 * repeats, which is what leaves the status as the only place the difference
 * lives". There is no field to read, and comparing `recordedAt` against a clock
 * would be guessing at a fact the server already stated.
 */
export const outcomeOfStatus = (status: number): RecordOutcome =>
  status === 200 ? 'already-recorded' : 'recorded';

/* -------------------------------------------------------------------------- */
/* The fields                                                                  */
/* -------------------------------------------------------------------------- */

/** The published bounds on `amountPaise` — `minimum: 1`, `maximum: 1e9`. */
const MIN_PAISE = 1;
const MAX_PAISE = 1_000_000_000;

/** `maxLength: 500` on `RecordExpenseDto.reason`, published. */
const REASON_MAX = 500;

export const OUTLET_REQUIRED = 'Choose the outlet the money left.';

export const AMOUNT_REQUIRED = 'Enter how much the expense was, in rupees.';

/**
 * No symbol and no separator, matching `RUPEES_SHAPE`. The minus sign is named
 * because it is the one somebody reaches for: an expense is money going out, and
 * the API refuses a negative amount rather than reading it as one — a correction
 * is a *new* expense naming the original (R-707), never a negative row.
 */
export const AMOUNT_MALFORMED =
  'An amount is rupees and paise — 450 or 450.50. No ₹, no commas, no minus sign, and no more than two decimal places.';

/**
 * **Zero is a refusal here**, unlike in a price field.
 *
 * `EXPENSE_AMOUNT_INVALID` covers "an amount that is zero, negative, fractional
 * or beyond the bound", and the floor the document publishes is one paisa.
 */
export const AMOUNT_TOO_SMALL = 'An expense is more than nothing — enter at least ₹0.01.';

export const AMOUNT_TOO_LARGE = `An expense is at most ${new Intl.NumberFormat('en-IN').format(MAX_PAISE / 100)} rupees. Check the figure.`;

export const AMOUNT_UNREPRESENTABLE =
  'That amount is larger than this screen can carry without rounding it. Check the figure.';

export const REASON_REQUIRED = 'Say what the money was for.';

/**
 * **A reason that is only whitespace is refused, and this is the message for
 * it.** The API checks "blank once trimmed" and a tab-only string is the case
 * that reaches the wire looking filled in — the field is not empty, so "say what
 * the money was for" would read as a screen that had not noticed the typing.
 *
 * It is a separate message from `REASON_REQUIRED` for that reason and no other.
 * The refusal is the same one on the server.
 */
export const REASON_BLANK =
  'A reason of spaces is not a reason. Say what the money was for — this cannot be edited afterwards.';

export const REASON_TOO_LONG = `A reason is at most ${String(REASON_MAX)} characters.`;

export const MODE_REQUIRED = 'Choose how the expense was paid.';

export const WHEN_REQUIRED = 'Say when the money went out.';

/** `maxLength: 50` on `RecordExpenseDto.receiptNumber`, published. */
const RECEIPT_MAX = 50;

export const RECEIPT_TOO_LONG = `A receipt number is at most ${String(RECEIPT_MAX)} characters.`;

/**
 * The amount, as a string, because that is what an `<Input>` holds.
 *
 * Deliberately **not** `z.coerce.number()`, for the reason `correction-form.ts`
 * gives about quantities: coercion reads `''` as `0`, so an untouched field
 * would earn "an expense is more than nothing" rather than "enter how much". The
 * text is checked first, so "you have not answered", "that is not an amount" and
 * "that amount is out of range" stay three sentences.
 *
 * The arithmetic runs on `paiseFromRupees`, which moves the digits as text and
 * never divides a decimal — `Number('450.50') * 100` is not 45050 on every
 * value, and `lib/money.ts` exists so that no screen has to know which ones.
 */
export const amountSchema = z
  .string()
  .trim()
  .min(1, AMOUNT_REQUIRED)
  .regex(RUPEES_SHAPE, AMOUNT_MALFORMED)
  .refine((rupees) => isRepresentablePaise(paiseFromRupees(rupees)), AMOUNT_UNREPRESENTABLE)
  .refine((rupees) => paiseFromRupees(rupees) >= MIN_PAISE, AMOUNT_TOO_SMALL)
  .refine((rupees) => paiseFromRupees(rupees) <= MAX_PAISE, AMOUNT_TOO_LARGE);

/**
 * What the money was for.
 *
 * Two checks and not one. `.min(1)` on the raw value catches the empty field;
 * the trimmed check catches the field holding only whitespace. Trimming *first*
 * and then taking `.min(1)` would collapse the two into one message, and they
 * are different things to tell somebody — one field has not been filled in and
 * the other looks filled in.
 *
 * The value is **not** trimmed on the way out. The API stores a reason "exactly
 * as submitted", and a client that quietly rewrote it would be editing a record
 * that cannot afterwards be edited. What leaves this form is what was typed.
 */
export const reasonSchema = z
  .string()
  .min(1, REASON_REQUIRED)
  .refine((value) => value.trim().length > 0, REASON_BLANK)
  .max(REASON_MAX, REASON_TOO_LONG);

/**
 * When the money went out.
 *
 * Built from `lib/local-instant.ts`'s parser and borrowing only its *malformed*
 * message, exactly as `correction-form.ts` does: that one is about calendars and
 * is true wherever a person types a date, while its required message reads
 * "choose the date and time to look up", which belongs to a screen looking
 * something up. This field is a fact being recorded.
 *
 * **No upper bound, deliberately.** The API accepts any instant on this field
 * and the trading day is the *server's* to resolve — the route takes no date at
 * all, precisely so nobody can move spending between days after the fact. A form
 * that refused a moment slightly ahead of a slow clock would be a rule invented
 * on one screen.
 */
const whenSchema = z
  .string()
  .trim()
  .min(1, WHEN_REQUIRED)
  .refine((local) => isoInstantFrom(local) !== null, INSTANT_MALFORMED);

/**
 * The whole form.
 *
 * `paymentMode` is a `string` here and an `ExpensePaymentMode` on the wire, and
 * the step between them is `isExpensePaymentMode` rather than a cast — the same
 * shape, and the same `: boolean` annotation, that `correction-form.ts` explains
 * at length. It has to be a string in the form because "nothing chosen yet" is
 * `''`, which no member of a closed set can stand for.
 *
 * **The outlet is here and not on the wire body.** It goes in the path (FR-705a)
 * and `RecordExpenseDto` does not define it at all — a body carrying it earns
 * `400 VALIDATION_UNKNOWN_FIELD`. It is a form field because a person has to
 * choose it; it is not part of `content` below for the same reason.
 */
export const expenseSchema = z.object({
  outletId: z.string().min(1, OUTLET_REQUIRED),
  amount: amountSchema,
  reason: reasonSchema,
  paymentMode: z
    .string()
    .min(1, MODE_REQUIRED)
    .refine((value): boolean => isExpensePaymentMode(value), MODE_REQUIRED),
  at: whenSchema,
  // Both optional (blossom-rest-api#512); `''` is "none" and is omitted on the
  // wire. The vendor picker offers active vendors only, so it needs no rule.
  vendorId: z.string(),
  receiptNumber: z.string().trim().max(RECEIPT_MAX, RECEIPT_TOO_LONG),
});

export type ExpenseValues = z.infer<typeof expenseSchema>;

/**
 * What this form is about to send, as one string.
 *
 * It covers the outlet in the path and everything in the body except the body's
 * own `id`, and that exception is not a gap: the id is minted *with* the key by
 * `attemptFor` in `lib/write-attempt.ts` and is therefore a function of this
 * value. Two attempts with equal fingerprints carry the same id as well as the
 * same key, so the raw bodies are byte-identical and the retry replays as a
 * `200` rather than writing a second expense.
 *
 * `JSON.stringify` over an array rather than an object, so the comparison cannot
 * turn on key order.
 */
export function fingerprintOf(outletId: string, body: Omit<RecordExpenseDto, 'id'>): string {
  return JSON.stringify([
    outletId,
    body.amountPaise,
    body.reason,
    body.paymentMode,
    body.occurredAt,
    body.vendorId ?? null,
    body.receiptNumber ?? null,
  ]);
}
